import PropTypes from 'prop-types';

import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { approveStockIssue } from '#/api/imsMgmt/receipts/stockIssue/approve';
import { cancelStockIssue } from '#/api/imsMgmt/receipts/stockIssue/cancel';
import { fetchStockIssueDetail } from '#/api/imsMgmt/receipts/stockIssue/fetchDetail';
import { rejectStockIssue } from '#/api/imsMgmt/receipts/stockIssue/reject';
import { submitStockIssue } from '#/api/imsMgmt/receipts/stockIssue/submit';
import SideMsg from '#/modules/commonComponents/SideMsg';
import DocumentSideMsgActions from '#/modules/imsMgmt/receipts/shared/DocumentSideMsgActions';
import { useDocumentSideMsgWorkflow } from '#/modules/imsMgmt/receipts/shared/useDocumentSideMsgWorkflow.jsx';
import {
  stockIssueSideMsgDetailBlock,
  stockIssueSideMsgStatusBlock,
} from '#/modules/imsMgmt/receipts/stockIssue/data/stockIssueSideMsgConfig';
import {
  getStockIssueApiErrorMessage,
  isStockIssueVersionConflict,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/apiError';
import {
  canApproveStockIssue,
  canCancelStockIssue,
  canRejectStockIssue,
  canSubmitStockIssue,
} from '#/modules/imsMgmt/receipts/stockIssue/helper/issueStatus';
import {
  stockIssueDetailQueryKey,
  stockIssueListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CONFIRM_COPY = {
  submit: {
    title: '提交出庫單',
    message: '提交後將鎖定來源庫存並進入待核准，確定提交？',
    confirmText: '提交',
  },
  approve: {
    title: '核准出庫單',
    message: '核准後將扣減庫存，確定核准？',
    confirmText: '核准',
  },
  reject: {
    title: '退回出庫單',
    message: '退回後將恢復為草稿並釋放鎖定，確定退回？',
    confirmText: '退回',
  },
  cancel: {
    title: '刪除草稿',
    message: '刪除後無法復原，確定刪除？',
    confirmText: '刪除',
  },
};

const APIS = {
  submit: submitStockIssue,
  approve: approveStockIssue,
  reject: rejectStockIssue,
  cancel: cancelStockIssue,
  fetchDetail: fetchStockIssueDetail,
};

const StockIssueSideMsg = ({ displayData, setSelectedData }) => {
  const canCreate = useCan(PERM.STOCK_ISSUE_CREATE);
  const canReview = useCan(PERM.STOCK_ISSUE_REVIEW);

  const workflow = useDocumentSideMsgWorkflow({
    row: displayData,
    setSelectedData,
    confirmCopy: CONFIRM_COPY,
    canSubmit: (status) => canCreate && canSubmitStockIssue(status),
    canApprove: (status) => canReview && canApproveStockIssue(status),
    canReject: (status) => canReview && canRejectStockIssue(status),
    canCancel: (status) => canCreate && canCancelStockIssue(status),
    apis: APIS,
    listQueryKeyPrefix: stockIssueListQueryKeyPrefix,
    detailQueryKey: stockIssueDetailQueryKey,
    getErrorMessage: getStockIssueApiErrorMessage,
    isVersionConflict: isStockIssueVersionConflict,
  });

  const hasActions = (
    workflow.canSubmit
    || workflow.canApprove
    || workflow.canReject
    || workflow.canCancel
  );

  const blocks = [
    stockIssueSideMsgStatusBlock,
    {
      condition: () => hasActions,
      fields: [
        {
          key: 'actions',
          label: '操作',
          render: () => (
            <div className="side_msg-row">
              <h3>操作</h3>
              <DocumentSideMsgActions
                canSubmit={workflow.canSubmit}
                canApprove={workflow.canApprove}
                canReject={workflow.canReject}
                canCancel={workflow.canCancel}
                isPending={workflow.isPending}
                onSubmit={workflow.handleSubmit}
                onApprove={workflow.handleApprove}
                onReject={workflow.handleReject}
                onCancel={workflow.handleCancel}
              />
            </div>
          ),
        },
      ],
    },
    stockIssueSideMsgDetailBlock,
  ];

  return (
    <>
      {workflow.ConfirmDialogHost}
      <workflow.RejectDialogHost />
      <SideMsg
        title="出庫單"
        titleValue="issueNo"
        displayData={displayData}
        blocks={blocks}
      />
    </>
  );
};

StockIssueSideMsg.propTypes = {
  displayData: PropTypes.object,
  setSelectedData: PropTypes.func.isRequired,
};

export default StockIssueSideMsg;
